import React from 'react';
import { Button } from './button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { cn } from '../../lib/utils';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  pageSizeOptions?: number[];
  showPageNumbers?: boolean;
  maxVisiblePages?: number;
  /** Extra text after "Showing X to Y of Z" (e.g. load progress). */
  rangeSuffix?: string;
  /** Override the "of N" number (e.g. server total while still loading pages). */
  displayTotalItems?: number;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageNumbers = true,
  maxVisiblePages = 5,
  rangeSuffix,
  displayTotalItems,
  className,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const ofTotal = displayTotalItems != null ? displayTotalItems : totalItems;

  const resolvedPageSizeOptions = React.useMemo(() => {
    const opts = [...pageSizeOptions];
    if (!opts.includes(itemsPerPage)) {
      opts.push(itemsPerPage);
      opts.sort((a, b) => a - b);
    }
    return opts;
  }, [pageSizeOptions, itemsPerPage]);

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    const newItemsPerPage = parseInt(value, 10);
    onItemsPerPageChange(newItemsPerPage);
    // Reset to first page when changing items per page
    onPageChange(1);
  };

  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 mt-4 flex min-h-10 flex-wrap items-center justify-between gap-y-2 border-t border-[#E2E5EA] bg-white/95 px-4 py-3 backdrop-blur',
        className
      )}
    >
      {/* Items info */}
      <p className="text-[12px] text-heritage-grey">
        Showing {startItem} to {endItem} of {ofTotal}
        {rangeSuffix ? (
          <span className="ml-1 inline-flex items-center gap-1.5 font-semibold text-blueprint-blue">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
            {rangeSuffix}
          </span>
        ) : null}
      </p>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <Button
          onClick={handlePrevious}
          disabled={currentPage === 1 || totalItems === 0}
          className="h-8 w-8 rounded-lg border-[#E2E5EA] p-0 hover:bg-page-bg disabled:cursor-not-allowed disabled:opacity-40"
          title="Previous Page"
          variant="outline"
          size="sm"
        >
          <ChevronLeft className="h-4 w-4 text-text-grey" />
        </Button>

        {/* Page numbers */}
        {showPageNumbers && (
          <div className="flex items-center gap-1">
            {visiblePages.map((page) => (
              <Button
                key={page}
                onClick={() => handlePageClick(page)}
                disabled={totalItems === 0}
                className={cn(
                  'h-8 min-w-8 rounded-lg border px-2 text-[12px] disabled:cursor-not-allowed disabled:opacity-40',
                  page === currentPage
                    ? 'border-hero-red bg-hero-red text-white hover:bg-hero-red-60'
                    : 'border-[#E2E5EA] bg-white text-text-grey hover:border-heritage-grey hover:bg-page-bg'
                )}
                variant="outline"
                size="sm"
              >
                {page}
              </Button>
            ))}
          </div>
        )}

        {/* Next button */}
        <Button
          onClick={handleNext}
          disabled={currentPage === totalPages || totalItems === 0}
          className="h-8 w-8 rounded-lg border-[#E2E5EA] p-0 hover:bg-page-bg disabled:cursor-not-allowed disabled:opacity-40"
          title="Next Page"
          variant="outline"
          size="sm"
        >
          <ChevronRight className="h-4 w-4 text-text-grey" />
        </Button>

        {/* Items per page selector */}
        <div className="ml-4 flex items-center gap-2">
          <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
            <SelectTrigger className="h-8 w-20 text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {resolvedPageSizeOptions.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[12px] text-heritage-grey whitespace-nowrap">/ page</span>
        </div>
      </div>
    </div>
  );
};
